import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, FlaskConical, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  ordered: "border-blue-200 bg-blue-50 text-blue-700",
  sample_collected: "border-amber-200 bg-amber-50 text-amber-700",
  result_entered: "border-violet-200 bg-violet-50 text-violet-700",
  approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-rose-200 bg-rose-50 text-rose-700",
};

export default function Lab() {
  const [items, setItems] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [open, setOpen] = useState(false);
  const [patientQ, setPatientQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({ patient_id: "", patient_name: "", items: [] });
  const [resultsFor, setResultsFor] = useState(null);
  const [resItems, setResItems] = useState([]);

  const load = async () => { const { data } = await api.get("/lab/orders"); setItems(data.items); };
  useEffect(() => {
    load();
    api.get("/masters/investigations").then(({ data }) => setInvestigations(data.items));
  }, []);

  const search = async (q) => { setPatientQ(q); if (q.length < 2) { setPatients([]); return; } const { data } = await api.get("/patients", { params: { q, limit: 8 } }); setPatients(data.items); };
  const addInvestigation = (id) => {
    const inv = investigations.find(i => i.id === id);
    if (!inv) return;
    setForm(f => ({ ...f, items: [...f.items, { investigation_id: inv.id, name: inv.name, normal_range: inv.normal_range, unit: inv.unit || "", result: "", flag: "" }] }));
  };
  const save = async () => {
    if (!form.patient_id || form.items.length === 0) { toast.error("Choose patient and add at least one test"); return; }
    try {
      await api.post("/lab/orders", { patient_id: form.patient_id, items: form.items });
      toast.success("Lab order created"); setOpen(false); load();
      setForm({ patient_id: "", patient_name: "", items: [] });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const updateStatus = async (id, s) => { await api.patch(`/lab/orders/${id}/status`, { status: s }); load(); };

  const openResults = (o) => { setResultsFor(o); setResItems(o.items || []); };
  const saveResults = async () => {
    if (!resultsFor) return;
    await api.post(`/lab/orders/${resultsFor.id}/results`, { items: resItems });
    toast.success("Results saved");
    setResultsFor(null); load();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">Laboratory</div>
          <h1 className="font-heading text-3xl font-semibold">Lab orders</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-700" data-testid="lab-new-btn"><Plus size={14} className="mr-2" />New order</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">New lab order</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div>
                <Label>Patient</Label>
                {form.patient_id ? (
                  <div className="flex items-center justify-between rounded-md border border-border p-2"><div>{form.patient_name}</div><Button size="sm" variant="outline" onClick={() => setForm({...form, patient_id: "", patient_name: ""})}>Change</Button></div>
                ) : (
                  <>
                    <Input value={patientQ} onChange={e => search(e.target.value)} placeholder="Search patient…" />
                    {patients.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-auto rounded-md border border-border">
                        {patients.map(p => <button key={p.id} className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary" onClick={() => { setForm(f => ({...f, patient_id: p.id, patient_name: p.name})); setPatients([]); setPatientQ(""); }}>{p.name} <span className="text-xs text-ink-muted">{p.uhid}</span></button>)}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label>Add investigation</Label>
                <Select onValueChange={addInvestigation}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{investigations.map(i => <SelectItem key={i.id} value={i.id}>{i.name} · ₹{i.price}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 text-sm">
                {form.items.map((it, i) => <div key={i} className="flex items-center justify-between rounded-md border border-border px-2 py-1"><span>{it.name}</span><Button size="sm" variant="ghost" onClick={() => setForm(f => ({...f, items: f.items.filter((_, idx) => idx !== i)}))}>Remove</Button></div>)}
              </div>
              <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="lab-save-btn">Create order</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
            <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Patient</th><th className="px-3 py-2 text-left">Tests</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-right">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan="5" className="px-3 py-10 text-center text-ink-muted">No orders yet.</td></tr>}
            {items.map(o => (
              <tr key={o.id}>
                <td className="px-3 py-2 text-xs">{(o.created_at||"").slice(0,16).replace("T"," ")}</td>
                <td className="px-3 py-2"><div className="font-medium">{o.patient_name}</div><div className="text-[11px] font-mono text-ink-muted">{o.patient_uhid}</div></td>
                <td className="px-3 py-2">{o.items?.map(i => i.name).join(", ")}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={STATUS[o.status] || ""}>{o.status.replace("_"," ")}</Badge></td>
                <td className="px-3 py-2 space-x-2 text-right">
                  {o.status === "ordered" && <Button size="sm" variant="outline" onClick={() => updateStatus(o.id, "sample_collected")}>Sample taken</Button>}
                  {o.status !== "approved" && <Button size="sm" variant="outline" onClick={() => openResults(o)} data-testid={`lab-results-${o.id.slice(0,6)}`}><FlaskConical size={12} className="mr-1" />Enter results</Button>}
                  {o.status === "result_entered" && <Button size="sm" className="bg-leaf hover:bg-leaf-600" onClick={() => updateStatus(o.id, "approved")}><CheckCircle2 size={12} className="mr-1" />Approve</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!resultsFor} onOpenChange={(v) => !v && setResultsFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Result entry — {resultsFor?.patient_name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {resItems.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <div className="col-span-4"><Label className="text-xs">{it.name}</Label></div>
                <Input className="col-span-3" placeholder="Result" value={it.result || ""} onChange={e => { const arr = [...resItems]; arr[i] = { ...arr[i], result: e.target.value }; setResItems(arr); }} />
                <Input className="col-span-2" placeholder="Unit" value={it.unit || ""} onChange={e => { const arr = [...resItems]; arr[i] = { ...arr[i], unit: e.target.value }; setResItems(arr); }} />
                <Input className="col-span-2" placeholder="Range" value={it.normal_range || ""} disabled />
                <Select value={it.flag || ""} onValueChange={(v) => { const arr = [...resItems]; arr[i] = { ...arr[i], flag: v }; setResItems(arr); }}>
                  <SelectTrigger className="col-span-1 h-8"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="normal">N</SelectItem><SelectItem value="high">H</SelectItem><SelectItem value="low">L</SelectItem><SelectItem value="critical">C</SelectItem></SelectContent>
                </Select>
              </div>
            ))}
            <Button onClick={saveResults} className="bg-brand hover:bg-brand-700">Save results</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
